import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Scale, ArrowLeft, Copy, Download, FileDown } from "lucide-react";
import { generateIRAC } from "@/lib/api/ai-research.functions";
import { CitationSetPicker } from "@/components/research/CitationSetPicker";
import type { CitationSet } from "@/lib/api/citation-sets.functions";
import { downloadResearchPdf } from "@/lib/research-pdf";

export const Route = createFileRoute("/education/research/irac")({
  head: () => ({
    meta: [
      { title: "IRAC Generator — Legal Research Hub | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "AI IRAC analyser: enter facts and a legal question, get a labelled Issue / Rule / Application / Conclusion analysis with citation verification flags.",
      },
    ],
  }),
  component: IracPage,
});

function IracPage() {
  const run = useServerFn(generateIRAC);
  const [facts, setFacts] = useState("");
  const [question, setQuestion] = useState("");
  const [jurisdiction, setJurisdiction] = useState("South Africa");
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [citationSetId, setCitationSetId] = useState<string | null>(null);
  const [citationSet, setCitationSet] = useState<CitationSet | null>(null);

  async function onRun(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    setOut(null);
    try {
      const r = await run({ data: { facts, question, jurisdiction, citationSetId: citationSetId ?? undefined } });
      setBalance(r.balance);
      if (r.ok) setOut(r.output);
      else setErr(r.error);
    } catch (e: any) {
      const msg = String(e?.message ?? "");
      if (msg.includes("INSUFFICIENT_CREDITS"))
        setErr("You've run out of AI credits. Buy more from your portal credits page.");
      else if (msg.includes("RATE_LIMIT")) setErr("Too many requests right now — try again in a minute.");
      else setErr("AI service is unavailable. Try again shortly.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-narrow py-10">
      <Link to="/education/research" className="inline-flex items-center gap-1 text-xs text-navy/70 hover:text-navy">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Research Hub
      </Link>
      <div className="mt-3 flex items-center gap-3">
        <span className="rounded-md bg-navy/5 p-2 text-navy"><Scale className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-3xl text-navy">IRAC Generator</h1>
          <p className="text-sm text-muted-foreground">1 credit per analysis</p>
        </div>
      </div>

      <div className="mt-6">
        <CitationSetPicker value={citationSetId} onChange={(id, set) => { setCitationSetId(id); setCitationSet(set); }} />
      </div>

      <form onSubmit={onRun} className="mt-4 space-y-4">
        <div>
          <label className="text-sm font-semibold text-navy">Legal question</label>
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
            minLength={8}
            maxLength={600}
            placeholder="e.g. Is the employer liable for the conduct of the security guard?"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-sm font-semibold text-navy">Jurisdiction</label>
          <input
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            maxLength={120}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-sm font-semibold text-navy">Material facts</label>
          <textarea
            value={facts}
            onChange={(e) => setFacts(e.target.value)}
            required
            minLength={40}
            maxLength={6000}
            rows={8}
            placeholder="Set out the facts the analysis must work from."
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-muted-foreground">{facts.length}/6000</p>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />}
          Generate IRAC (1 credit)
        </button>
      </form>

      {balance !== null && (
        <p className="mt-3 text-xs text-muted-foreground">Credits remaining: <strong>{balance}</strong></p>
      )}
      {err && <p className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-800">{err}</p>}

      {out && (
        <article className="mt-6 rounded-lg border border-input bg-card p-5">
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            <button
              onClick={() =>
                downloadResearchPdf(
                  {
                    title: `IRAC — ${question.slice(0, 80) || "Legal question"}`,
                    subtitle: `Jurisdiction: ${jurisdiction}`,
                    citationSetName: citationSet?.name,
                    citationStyle: citationSet?.style ?? "OSCOLA",
                    body: out,
                  },
                  `irac-analysis-oscola.pdf`,
                )
              }
              className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
            >
              <FileDown className="h-3.5 w-3.5" /> Download PDF
            </button>
            <button
              onClick={() => {
                const blob = new Blob([out], { type: "text/markdown" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `irac-analysis-oscola.md`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted"
            >
              <Download className="h-3.5 w-3.5" /> .md
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(out)}
              className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted"
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">{out}</pre>
          <p className="mt-4 border-t border-border pt-3 text-xs italic text-muted-foreground">
            AI-generated. Verify every authority and citation against the primary source before relying on it.
          </p>
        </article>
      )}
    </div>
  );
}
